import React, { useState, useEffect } from 'react';
import { FaUsers, FaUser, FaBookOpen, FaGraduationCap, FaHeart, FaGlobe, FaChurch, FaSpinner } from 'react-icons/fa';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../shared/firebase';
import { extractString, extractOrder } from '../../shared/fieldUtils';

function getRouteAndIcon(name = '') {
  const n = name.toLowerCase();
  if (n.includes('active') || (n.includes('presbyter') && !n.includes('study') && !n.includes('retired'))) {
    return { route: 'presbyters', icon: FaUsers };
  }
  if (n.includes('deacon')) return { route: 'deacons', icon: FaUser };
  if (n.includes('evangelist') && !n.includes('retired')) return { route: 'evangelists', icon: FaHeart };
  if (n.includes('church worker') && !n.includes('retired')) return { route: 'church-workers', icon: FaUsers };
  if (n.includes('deputation')) return { route: 'deputation', icon: FaBookOpen };
  if (n.includes('study')) return { route: 'presbyters-on-study', icon: FaGraduationCap };
  if (n.includes('seminary') || n.includes('student')) return { route: 'seminary-students', icon: FaBookOpen };
  if (n.includes('retired presbyter')) return { route: 'retired-presbyters', icon: FaUser };
  if (n.includes('missionar')) return { route: 'missionaries', icon: FaGlobe };
  if (n.includes('bishop') || n.includes('pioneer')) return { route: 'former-bishops', icon: FaChurch };
  if (n.includes('retired')) return { route: 'retired-evangelists', icon: FaHeart };
  return { route: n.replace(/[^a-z0-9]+/g, '-'), icon: FaUsers };
}

export default function LightsOfDiocese({ activeSection, onSelectSection }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    async function fetchLights() {
      try {
        let snap = await getDocs(collection(db, 'diocese-lights')).catch(() => null);
        if (!snap || snap.empty) {
          snap = await getDocs(collection(db, 'diocese_lights')).catch(() => null);
        }
        if (!snap || snap.empty) {
          snap = await getDocs(collection(db, 'lights-of-diocese')).catch(() => null);
        }

        if (snap && !snap.empty) {
          const list = snap.docs.map(doc => {
            const data = doc.data();
            const name = extractString(data.name || data.title);
            const mapped = getRouteAndIcon(name);
            return {
              id: doc.id,
              title: name,
              route: extractString(data.route) || mapped.route,
              icon: mapped.icon,
              order: extractOrder(data.order)
            };
          });
          list.sort((a, b) => a.order - b.order || a.title.localeCompare(b.title));
          if (isMounted) setGroups(list);
        } else {
          if (isMounted) setGroups([]);
        }
      } catch (err) {
        console.error("Error fetching diocese lights:", err);
        if (isMounted) setGroups([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    fetchLights();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF7F2] text-[#5F5A58] font-sans flex flex-col pb-16">
      
      {/* Header Section */}
      <section className="relative bg-gradient-to-b from-[#FAF6F2] via-[#F5EFEE] to-[#E8DDD7] pt-28 sm:pt-32 pb-16 sm:pb-20 overflow-hidden border-b border-[#E8DDD7]">
        <div className="absolute right-[5%] sm:right-[10%] top-1/2 -translate-y-1/2 pointer-events-none opacity-[0.03] select-none">
          <FaChurch className="w-64 h-64 sm:w-96 sm:h-96 text-[#7B1E3A]" />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 text-center md:text-left flex flex-col md:items-start items-center">
          <div className="w-16 h-1 bg-[#D4A64A] mb-6 rounded-full"></div>
          <h1 className="font-serif font-bold tracking-[0.03em] text-[#7B1E3A] text-[38px] sm:text-[50px] md:text-[58px] lg:text-[64px] leading-[1.05] uppercase">
            Lights of our Diocese
          </h1>
          <p className="mt-4 sm:mt-5 max-w-xl text-[#7B6B66] text-[15px] sm:text-[17px] leading-[1.6] font-normal">
            Honoring the spiritual leaders, missionaries, and dedicated workers in the vineyard.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-6 sm:px-8 lg:px-12 mt-16 w-full space-y-16">
        
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-[#7B1E3A] font-serif font-bold text-3xl sm:text-4xl mb-4">Ministries of the Diocese</h2>
          <p className="text-[#5F5A58] text-[16px] sm:text-[17px] leading-[1.85]">
            The Diocese of Malabar is immensely blessed by the dedicated service of our spiritual leaders, missionaries, and workers who faithfully serve and have served in the vineyard of the Lord.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-[24px] border border-[#E8DDD7]">
            <FaSpinner className="w-10 h-10 text-[#7B1E3A] animate-spin mb-3" />
            <p className="text-sm font-semibold text-[#5A4F4A]">Loading Ministries...</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-[24px] border border-[#E8DDD7] p-8">
            <h3 className="font-serif font-bold text-xl text-[#7B1E3A]">No Categories Found</h3>
            <p className="text-sm text-[#7B6B66] mt-1">Please check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {groups.map((group, idx) => {
              const Icon = group.icon;
              return (
                <div 
                  key={group.id || idx} 
                  onClick={() => onSelectSection(group.route)}
                  className="bg-white rounded-[24px] border border-[#E8DDD7] p-6 shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-4 group cursor-pointer hover:-translate-y-1"
                >
                  <div className="w-14 h-14 shrink-0 rounded-2xl bg-[#FAF6F2] text-[#7B1E3A] flex items-center justify-center group-hover:bg-[#7B1E3A] group-hover:text-white transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-serif font-bold text-[#7B1E3A] text-lg leading-tight group-hover:text-[#D4A64A] transition-colors">
                    {group.title}
                  </h3>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center pb-8 pt-8">
          <button 
            onClick={() => onSelectSection('about')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#7B1E3A] hover:bg-[#5E132A] text-white font-sans font-bold text-sm tracking-wider uppercase rounded-full transition-all duration-300 shadow-md"
          >
            Return to About Diocese
          </button>
        </div>

      </div>
    </div>
  );
}
